import React from "react";
import classnames from "classnames";

import styles from "./KnowledgeDefinitionTable.module.css";
import {useAppDispatch} from "../../../../app/hooks";
import {FormName, openForm} from "../../../elements/formProvider/formSlice";
import {openPopupModal, PopupModalName} from "../../../elements/popupModalProvider/popupModalSlice";
import {EditButton} from "../../../elements/editButton/EditButton";
import {DeleteButton} from "../../../elements/deleteButton/DeleteButton";
import {TableLoader} from "../../../elements/tableLoader/TableLoader";
import {KnowledgeDefinitionKind, KnowledgeDefinitionUi} from "../../knowledge/knowledgeDefinitionsApi";
import {KNOWLEDGE_DEFINITION_LABELS} from "../knowledgeDefinitionLabels";
import {TablePagination} from "../../../elements/pagination/TablePagination";
import {useClientPagination} from "../../../../hooks/useClientPagination";

export const KNOWLEDGE_DEFINITIONS_ON_PAGE = 10;

interface Props {
    kind: KnowledgeDefinitionKind;
    definitions: KnowledgeDefinitionUi[];
    isLoading: boolean;
}

/** Built-in knowledge types have no publicId and cannot be edited or deleted. */
const isBuiltIn = (definition: KnowledgeDefinitionUi): boolean =>
    "builtIn" in definition && definition.builtIn;

export const KnowledgeDefinitionTable: React.FC<Props> = ({kind, definitions, isLoading}) => {
    const dispatch = useAppDispatch();
    const labels = KNOWLEDGE_DEFINITION_LABELS[kind];
    const hasCustomDefinitions = definitions.some(definition => !isBuiltIn(definition));
    // The whole catalogue is loaded anyway (the knowledge editors need it for their pickers), so it is paged here.
    const {pageNumber, setPageNumber, pageItems, totalItems} =
        useClientPagination(definitions, KNOWLEDGE_DEFINITIONS_ON_PAGE, kind);

    const openEdit = (definition: KnowledgeDefinitionUi) =>
        dispatch(openForm({name: FormName.KNOWLEDGE_DEFINITION, formProps: {kind, definition}}));

    const openDelete = (definition: KnowledgeDefinitionUi) =>
        dispatch(openPopupModal({
            name: PopupModalName.DELETE_KNOWLEDGE_DEFINITION,
            popupModalProps: {kind, publicId: definition.publicId, entityName: definition.name}
        }));

    return <div className={styles.wrapper}>
        <div className={styles.tableWrapper}>
        <div className={styles.headerRow}>
            <span className={styles.nameCell}>Name</span>
            <span className={styles.keyCell}>Key</span>
            <span className={styles.descriptionCell}>Description</span>
            <span className={styles.actionsCell}>Actions</span>
        </div>
        <div className={styles.rowsWrapper}>
            {isLoading && <TableLoader/>}
            {!isLoading && pageItems.map(definition =>
                <div
                    className={classnames(styles.row, isBuiltIn(definition) && styles.builtInRow)}
                    key={definition.publicId || definition.key}
                    data-testid={`knowledge-definition-${definition.key}`}
                >
                    <span className={styles.nameCell}>{definition.name}</span>
                    <span className={styles.keyCell}><code>{definition.key}</code></span>
                    <span className={styles.descriptionCell}>{definition.description}</span>
                    <span className={styles.actionsCell}>
                        {isBuiltIn(definition)
                            ? <span className={styles.builtInBadge}>Built-in</span>
                            : <span className={styles.actions}>
                                <EditButton handleClick={() => openEdit(definition)}/>
                                <DeleteButton handleClick={() => openDelete(definition)}/>
                            </span>
                        }
                    </span>
                </div>
            )}
            {!isLoading && !hasCustomDefinitions &&
                <div className={styles.emptyState}>{labels.emptyState}</div>
            }
        </div>
        </div>
        {!isLoading && totalItems > 0 && <TablePagination
            pageNumber={pageNumber}
            itemsPerPage={KNOWLEDGE_DEFINITIONS_ON_PAGE}
            totalItems={totalItems}
            onPageChange={setPageNumber}
        />}
    </div>
}
